<template>
  <div class="bg-white py-16">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="section-title">Suivez-nous</h2>
        <p class="section-subtitle">
          Découvrez nos dernières actualités et moments musicaux sur les réseaux sociaux
        </p>
      </div>
      
      <!-- Loading state -->
      <div v-if="loading" class="flex justify-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
      
      <!-- Error state -->
      <div v-else-if="error" class="text-center text-gray-500">
        <p>Impossible de charger les publications. Veuillez réessayer plus tard.</p>
        <button @click="loadPosts" class="btn-primary mt-4">Réessayer</button>
      </div>
      
      <!-- Posts grid -->
      <div v-else-if="posts.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="post in posts" 
          :key="post.id"
          class="card hover:shadow-lg transition-shadow duration-300"
        >
          <div v-if="post.image_url" class="mb-4">
            <img 
              :src="post.image_url" 
              :alt="post.text.substring(0, 50)"
              class="w-full h-48 object-cover rounded-lg"
            />
          </div>
          
          <div class="flex items-center mb-3">
            <div class="flex-shrink-0">
              <div 
                class="w-8 h-8 rounded-full flex items-center justify-center"
                :class="post.platform === 'instagram' ? 'bg-pink-500' : 'bg-blue-600'"
              >
                <svg v-if="post.platform === 'instagram'" class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z"/>
                </svg>
                <svg v-else class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-900">
                {{ post.platform === 'instagram' ? 'Instagram' : 'Facebook' }}
              </p>
              <p class="text-xs text-gray-500">
                {{ formatDate(post.created_time) }}
              </p>
            </div>
          </div>
          
          <p class="text-gray-700 text-sm mb-4 line-clamp-3">
            {{ post.text }}
          </p>
          
          <a 
            :href="post.permalink" 
            target="_blank" 
            rel="noopener noreferrer"
            class="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Voir sur {{ post.platform === 'instagram' ? 'Instagram' : 'Facebook' }} →
          </a>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-else class="text-center text-gray-500">
        <p>Aucune publication disponible pour le moment.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { socialMediaService } from '../services/socialMediaService'

export default {
  name: 'SocialFeed',
  data() {
    return {
      posts: [],
      loading: true,
      error: false
    }
  },
  async mounted() {
    await this.loadPosts()
  },
  methods: {
    async loadPosts() {
      try {
        this.loading = true
        this.error = false
        this.posts = await socialMediaService.getAllSocialPosts(6)
      } catch (error) {
        console.error('Error loading social posts:', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
